import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { splitLocalePath } from "./lib/locale";

/**
 * Language addresses. On the server the prefix is already stripped before the
 * page is rendered (src/server.ts), so the router always works in English
 * paths. In the browser the address still carries the prefix, so the router is
 * told to treat /de, /fr, /es or /zh-cn as the root — that way every link keeps
 * the visitor in the language they chose, and the page they land on matches the
 * page the server rendered.
 */
function localeBasepath(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const { locale } = splitLocalePath(window.location.pathname);
  return locale === "en" ? undefined : `/${locale}`;
}

export const getRouter = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Data is reused for a minute instead of being re-fetched by every
        // component that asks for it — this removes most duplicate requests.
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        // Retry transient network/server problems only, with backoff, so a
        // slow or flaky connection recovers instead of showing an error.
        retry: (failureCount, error) => {
          const status = (error as { status?: number })?.status;
          if (typeof status === "number" && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: { retry: 0 },
    },
  });



  const router = createRouter({
    routeTree,
    context: { queryClient },
    basepath: localeBasepath(),
    // One router-owned scroll lifecycle for every navigation surface.
    // Push/replace navigations reset to the top, popstate restores the saved
    // reading position, and hash navigations still scroll to their target.
    scrollRestoration: true,
    scrollRestorationBehavior: "instant",
    scrollToTopSelectors: ["window", "[data-scroll-container]"],
    defaultPreloadStaleTime: 0,
  });

  // The framework re-applies its build-time base path when the page starts up
  // in the browser, which would drop the language prefix and leave a blank
  // page. Keep the prefix pinned for the life of this page.
  const basepath = localeBasepath();
  if (basepath) {
    const update = router.update.bind(router);
    router.update = ((opts: Parameters<typeof update>[0]) =>
      update({ ...opts, basepath })) as typeof router.update;
  }

  return router;
};
